import React, { useState, useMemo } from 'react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import { Lock, X, Check, AlertCircle, Banknote, Wallet, Info } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { BANKS } from '../../data/bankData';

const DURATIONS = [24, 36, 48];

/**
 * Confirmation modal for early lease buyout.
 * Two payment modes:
 *   1. Cash (treasury) — historical behaviour.
 *   2. Mortgage-backed bank loan — uses the truck as collateral, -0.4 pp on
 *      the offered rate. Cash-neutral today; the vehicle becomes
 *      "mortgaged" and cannot be sold until the loan is repaid.
 */
export const LeaseBuyoutModal = ({ vehicle, onClose }) => {
  const { t } = useTranslation();
  const computeLeaseBuyout = useGameStore(s => s.computeLeaseBuyout);
  const buyoutLease = useGameStore(s => s.buyoutLease);
  const buyoutLeaseWithLoan = useGameStore(s => s.buyoutLeaseWithLoan);
  const requestBuyoutLoanProposals = useGameStore(s => s.requestBuyoutLoanProposals);
  const treasury = useGameStore(s => s.treasury);

  const quote = computeLeaseBuyout(vehicle.id);
  const [mode, setMode] = useState('cash'); // 'cash' | 'loan'
  const [duration, setDuration] = useState(36);
  const [error, setError] = useState(null);

  // Advisory: suggest "Crédit" when paying cash would drop treasury < 5k €
  // OR when the buyout total represents > 60 % of current treasury.
  const cashWouldHurt = quote && (
    (treasury - quote.total < 5000) || (quote.total > treasury * 0.6)
  );

  // Loan proposals from the 3 banks for the current duration.
  const proposals = useMemo(() => {
    if (mode !== 'loan' || !quote) return [];
    return requestBuyoutLoanProposals
      ? requestBuyoutLoanProposals(vehicle.id, duration)
      : [];
  }, [mode, duration, vehicle.id, quote, requestBuyoutLoanProposals]);

  if (!quote) return null;
  const canAfford = treasury >= quote.total;
  const projectedTreasury = treasury - quote.total;

  const handleCashConfirm = () => {
    const result = buyoutLease(vehicle.id);
    if (result) onClose();
  };

  const handleLoanConfirm = (bankId) => {
    setError(null);
    const result = buyoutLeaseWithLoan(vehicle.id, bankId, duration);
    if (result && result.ok) {
      onClose();
    } else {
      setError(result?.bankReason || result?.reason || 'Échec de la signature du crédit.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="lease-buyout-overlay"
    >
      <div
        className="bg-ink-800 rounded-xl w-full max-w-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200 max-h-[92vh] flex flex-col"
        data-testid="lease-buyout-modal"
      >
        <div className="bg-purple-600 px-5 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 text-white">
            <Lock className="w-5 h-5" />
            <h3 className="font-bold text-base">{t('buyout_lease_title')}</h3>
          </div>
          <button aria-label="Fermer" onClick={onClose} className="text-white/70 hover:text-white" data-testid="close-buyout-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <div>
            <p className="text-sm font-semibold text-slate-200">{vehicle.brand} {vehicle.model}</p>
            <p className="text-[11px] text-slate-500">Tier {vehicle.tier} · {quote.monthsRemaining} {t('months_remaining')}</p>
          </div>

          <p className="text-[12px] text-slate-400 leading-snug">{t('buyout_lease_intro')}</p>

          <div className="rounded-md border border-ink-600 bg-ink-700/30 divide-y divide-ink-600 text-xs">
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-400">{t('buyout_lease_monthly_left')}</span>
              <span className="font-bold text-slate-200" data-testid="buyout-remaining-monthlies">
                {quote.monthsRemaining} × {formatMoney(quote.monthly)} = {formatMoney(quote.remainingMonthlies)}
              </span>
            </div>
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-400">{t('buyout_lease_residual')}</span>
              <span className="font-bold text-slate-200" data-testid="buyout-residual">{formatMoney(quote.residual)}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-slate-400">{t('buyout_lease_penalty')}</span>
              <span className="font-bold text-danger" data-testid="buyout-penalty">+ {formatMoney(quote.penalty)}</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2.5 bg-purple-500/10">
              <span className="text-sm font-semibold text-purple-300">{t('buyout_lease_total')}</span>
              <span className="text-base font-black text-purple-300" data-testid="buyout-total">{formatMoney(quote.total)}</span>
            </div>
          </div>

          {/* Smart advice */}
          {cashWouldHurt && mode === 'cash' && (
            <div className="rounded-md border border-warn/40 bg-warn/5 px-3 py-2 flex items-start gap-2" data-testid="buyout-credit-advice">
              <Info className="w-4 h-4 text-warn flex-shrink-0 mt-0.5" />
              <div className="text-[11.5px] text-warn leading-snug">
                <p className="font-semibold">Astuce DAF — préservez votre trésorerie</p>
                <p className="text-warn/90 mt-0.5">
                  Régler {formatMoney(quote.total)} cash laisserait votre trésorerie à <strong>{formatMoney(projectedTreasury)}</strong>.
                  Un crédit hypothécaire (camion en garantie) étale la sortie sur 2-4 ans à un taux préférentiel (−0,4 pp).
                </p>
                <button
                  onClick={() => setMode('loan')}
                  className="mt-1.5 text-[11px] font-bold text-warn underline hover:text-warn/80"
                  data-testid="switch-to-loan-btn"
                >
                  Voir les offres bancaires →
                </button>
              </div>
            </div>
          )}

          {/* Payment mode toggle */}
          <div className="flex gap-2" data-testid="payment-mode-toggle">
            <button
              onClick={() => setMode('cash')}
              className={`flex-1 px-3 py-2 rounded-md border-2 text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                mode === 'cash'
                  ? 'border-purple-500 bg-purple-500/15 text-purple-200'
                  : 'border-ink-600 text-slate-400 hover:border-ink-500'
              }`}
              data-testid="mode-cash"
            >
              <Wallet className="w-3.5 h-3.5" />
              Paiement comptant
            </button>
            <button
              onClick={() => setMode('loan')}
              className={`flex-1 px-3 py-2 rounded-md border-2 text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
                mode === 'loan'
                  ? 'border-money bg-money/15 text-money'
                  : 'border-ink-600 text-slate-400 hover:border-ink-500'
              }`}
              data-testid="mode-loan"
            >
              <Banknote className="w-3.5 h-3.5" />
              Crédit hypothécaire
            </button>
          </div>

          {/* === CASH MODE === */}
          {mode === 'cash' && (
            <>
              <div className="rounded-md border border-ink-600 bg-ink-700/30 p-2.5 text-xs">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">{t('treasury')} (post-rachat)</p>
                <p className={`text-base font-bold mt-0.5 ${projectedTreasury < 0 ? 'text-danger' : 'text-money'}`} data-testid="buyout-projected-treasury">
                  {formatMoney(projectedTreasury)}
                </p>
              </div>

              {!canAfford && (
                <div className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-danger flex-shrink-0" />
                  <p className="text-[12px] text-danger">{t('buyout_lease_insufficient')}</p>
                </div>
              )}
            </>
          )}

          {/* === LOAN MODE === */}
          {mode === 'loan' && (
            <div className="space-y-3" data-testid="loan-mode-panel">
              <div className="rounded-md border border-money/30 bg-money/5 p-3 flex items-start gap-2">
                <Banknote className="w-4 h-4 text-money flex-shrink-0 mt-0.5" />
                <div className="text-[11.5px] text-slate-300 leading-snug">
                  <p className="font-semibold text-money">Hypothèque sur le véhicule</p>
                  <p className="text-slate-400 mt-0.5">
                    Le camion sert de garantie. Vous obtenez <strong className="text-money">−0,4 pp</strong> sur le taux et le plafond d'endettement est relevé de <strong>+0,5×</strong>.
                    En contrepartie, le véhicule ne pourra pas être revendu tant que le crédit n'est pas soldé.
                  </p>
                </div>
              </div>

              {/* Duration selector */}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Durée du crédit</p>
                <div className="grid grid-cols-3 gap-2">
                  {DURATIONS.map(d => (
                    <button
                      key={d}
                      onClick={() => setDuration(d)}
                      className={`py-2 rounded-md border-2 text-xs font-bold transition-all ${
                        duration === d
                          ? 'border-money bg-money/15 text-money'
                          : 'border-ink-600 text-slate-400 hover:border-ink-500'
                      }`}
                      data-testid={`duration-${d}`}
                    >
                      {d} mois
                    </button>
                  ))}
                </div>
              </div>

              {/* Bank proposals */}
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Offres bancaires ({proposals.length})</p>
                {proposals.length === 0 && (
                  <p className="text-[12px] text-slate-500">Aucune banque disponible.</p>
                )}
                {proposals.map((p) => {
                  const bank = BANKS[p.bankId];
                  if (!bank) return null;
                  return (
                    <div
                      key={p.bankId}
                      className={`rounded-md border p-3 ${p.eligible ? 'border-ink-600 bg-ink-700/30' : 'border-danger/30 bg-danger/5'}`}
                      data-testid={`proposal-${p.bankId}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-bold text-slate-200" style={{ color: bank.color }}>
                          {bank.name}
                        </span>
                        {p.eligible && (
                          <span className="text-[11px] font-mono text-money font-bold">{p.ratePercent} %</span>
                        )}
                      </div>
                      {p.eligible ? (
                        <>
                          <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
                            <div>
                              <p className="text-slate-500">Mensualité</p>
                              <p className="text-slate-200 font-bold">{formatMoney(p.monthly)}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Coût total</p>
                              <p className="text-slate-200 font-bold">{formatMoney(p.totalCost)}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Intérêts</p>
                              <p className="text-warn font-bold">{formatMoney(p.totalInterest)}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleLoanConfirm(p.bankId)}
                            className="mt-2 w-full bg-money text-ink-900 px-3 py-1.5 rounded-md text-xs font-bold hover:bg-money/85 transition-colors flex items-center justify-center gap-1.5"
                            data-testid={`sign-loan-${p.bankId}`}
                          >
                            <Check className="w-3.5 h-3.5" />
                            Signer chez {bank.name}
                          </button>
                        </>
                      ) : (
                        <p className="text-[11px] text-danger" data-testid={`proposal-refused-${p.bankId}`}>
                          {p.reason}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {error && (
                <div className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 flex items-center gap-2" data-testid="loan-error">
                  <AlertCircle className="w-4 h-4 text-danger flex-shrink-0" />
                  <p className="text-[12px] text-danger">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-ink-600 bg-ink-700/30 flex items-center justify-between flex-shrink-0">
          <button
            onClick={onClose}
            className="text-xs text-slate-400 hover:text-slate-200 px-3 py-2 transition-colors"
            data-testid="cancel-buyout-btn"
          >
            {t('cancel')}
          </button>
          {mode === 'cash' && (
            <button
              onClick={handleCashConfirm}
              disabled={!canAfford}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="confirm-buyout-btn"
            >
              <Check className="w-4 h-4" />
              {t('buyout_lease_confirm')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaseBuyoutModal;
